import { CSRF_HEADER, type Residency, type ResidencyReport } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Lock, MapPin } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ok } from "../api-errors.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";

const KEY = "models.residency";
const residencyKey = ["admin", "residency"] as const;

export function useResidencyReport(enabled = true) {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: residencyKey,
    enabled,
    queryFn: async (): Promise<ResidencyReport> => ok(await admin.residency.$get()),
    // A 403 means "not an admin": the link and the screen simply stay away. Saves invalidate
    // it; nothing else needs a refetch on focus.
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Backoffice residency (spec `tenant-administration` "Residency administration"): per level in
 * the admin's scope, the resolved value, where it comes from and whether it is locked; tenant and
 * entity levels can be set and locked here. Projects only narrow, from their own settings.
 */
export function ResidencyScreen() {
  const { readOnly } = useAdminApi();
  const { t } = useTranslation();
  const report = useResidencyReport();

  if (report.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }
  if (report.isError) {
    return <Notice tone="danger">{report.error.message}</Notice>;
  }
  const data = report.data;
  const globalGranted = data.geographies.includes("global");
  const entityNames = new Map(data.entities.map((e) => [e.entityId, e.name]));
  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t("residency.title")}
        lead={
          <div className="flex flex-col gap-2">
            <p>{t("residency.intro")}</p>
            <p className="font-medium text-fg" data-testid="global-count">
              {t("residency.globalCount", { count: data.globalProjects })}
            </p>
          </div>
        }
      />
      {globalGranted ? null : <Notice>{t("residency.ceilingEuOnly")}</Notice>}

      <section aria-labelledby="residency-levels" className="flex flex-col gap-3">
        <h2 id="residency-levels" className="text-sm font-semibold text-fg">
          {t("residency.entities")}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
          <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
            <thead className="border-b border-line text-xs text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("residency.name")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("residency.residency")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("residency.origin")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  <span className="sr-only">{t("residency.save")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.tenant ? (
                <LevelRow
                  key={stateKey("tenant", data.tenant)}
                  name={t("residency.organisation")}
                  entityId={null}
                  view={data.tenant}
                  editable={!data.tenant.locked && !readOnly}
                />
              ) : null}
              {data.entities.map((e) => (
                <LevelRow
                  key={stateKey(e.entityId, e)}
                  name={e.name}
                  entityId={e.entityId}
                  view={e}
                  editable={!e.locked && !readOnly}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="residency-projects" className="flex flex-col gap-3">
        <h2 id="residency-projects" className="text-sm font-semibold text-fg">
          {t("residency.projects")}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
          <table className="w-full min-w-[32rem] border-collapse text-left text-sm">
            <thead className="border-b border-line text-xs text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("residency.name")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("residency.residency")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("residency.origin")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.projects.map((p) => (
                <tr key={p.projectId}>
                  <td className="px-4 py-2.5 text-fg">
                    {p.name ?? (
                      <span className="text-fg-muted">{t("residency.personalProject")}</span>
                    )}
                    {p.ownerEntityId && entityNames.has(p.ownerEntityId) ? (
                      <span className="text-fg-muted"> · {entityNames.get(p.ownerEntityId)}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5">
                    <ResidencyValue value={p.value} />
                  </td>
                  <td className="px-4 py-2.5 text-fg-secondary">
                    {t(`residency.source.${p.source}`)}
                    {p.locked ? ` · ${t("residency.lockedAbove")}` : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/**
 * A row's form starts from the server's values: when a refetch changes them (an inherited value
 * moved, a lock was set), the row remounts instead of keeping a stale choice.
 */
const stateKey = (
  id: string,
  v: { value: string; source: string; locked: boolean; lockedHere: boolean },
) => `${id}:${v.value}:${v.source}:${v.locked}:${v.lockedHere}`;

function ResidencyValue({ value }: { value: Residency }) {
  const { t } = useTranslation();
  const Icon = value === "global" ? Globe : MapPin;
  return (
    <span
      className={
        value === "global"
          ? "inline-flex items-center gap-1.5 font-medium text-fg"
          : "inline-flex items-center gap-1.5 text-fg-secondary"
      }
    >
      <Icon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      {t(`residency.value.${value}`)}
    </span>
  );
}

/** One tenant or entity level: its resolved value, and a set-and-lock form when writable. */
function LevelRow(props: {
  name: string;
  entityId: string | null;
  view: {
    value: Residency;
    source: "instance" | "tenant" | "entity" | "project";
    locked: boolean;
    lockedHere: boolean;
  };
  editable: boolean;
}) {
  const { admin } = useAdminApi();
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const id = useId();
  const [value, setValue] = useState<Residency>(props.view.value);
  const [locked, setLocked] = useState(props.view.lockedHere);
  const save = useMutation({
    mutationFn: async () => {
      const json = { value, locked };
      const headers = { headers: { [CSRF_HEADER]: csrfToken } };
      return props.entityId === null
        ? ok(await admin.settings[":key"].$put({ param: { key: KEY }, json }, headers))
        : ok(
            await admin.entities[":entityId"].settings[":key"].$put(
              { param: { entityId: props.entityId, key: KEY }, json },
              headers,
            ),
          );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: residencyKey }),
  });
  return (
    <tr className="align-middle">
      <td className="px-4 py-2.5 text-fg">{props.name}</td>
      <td className="px-4 py-2.5">
        {props.editable ? (
          <NativeSelect
            aria-label={t("residency.choose", { name: props.name })}
            value={value}
            onChange={(e) => setValue(e.target.value as Residency)}
            className="w-40"
          >
            <option value="eu">{t("residency.value.eu")}</option>
            <option value="global">{t("residency.value.global")}</option>
          </NativeSelect>
        ) : (
          <ResidencyValue value={props.view.value} />
        )}
      </td>
      <td className="px-4 py-2.5 text-fg-secondary">
        <span className="inline-flex flex-wrap items-center gap-x-1.5">
          {t(`residency.source.${props.view.source}`)}
          {props.view.locked ? (
            <span className="inline-flex items-center gap-1">
              · <Lock aria-hidden="true" className="size-3" />
              {t("residency.lockedAbove")}
            </span>
          ) : null}
          {props.view.lockedHere ? (
            <span className="inline-flex items-center gap-1">
              · <Lock aria-hidden="true" className="size-3" />
              {t("residency.locked")}
            </span>
          ) : null}
        </span>
      </td>
      <td className="px-4 py-2.5">
        {props.editable ? (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${id}-lock`}
                checked={locked}
                onCheckedChange={(on) => setLocked(on === true)}
              />
              <Label htmlFor={`${id}-lock`} className="font-normal text-fg-secondary">
                {t("residency.lock")}
              </Label>
            </div>
            <Button
              variant="outline"
              size="sm"
              // Only a change is written: saving an inherited row would pin it.
              disabled={
                save.isPending || (value === props.view.value && locked === props.view.lockedHere)
              }
              onClick={() => save.mutate()}
            >
              {save.isPending ? t("residency.saving") : t("residency.save")}
            </Button>
            {save.isError ? <InlineError>{save.error.message}</InlineError> : null}
          </div>
        ) : null}
      </td>
    </tr>
  );
}
